import type * as hast from 'hast';

import type { Block, Document, Fragment, Inline } from '@gitbook/slate';

import { footnoteIdToHtmlId } from '../../utils';
import type { FormattingContext } from '../context';
import { gastToAst } from '../utils';
import { transformBlocks } from './transform-blocks';
import { transformInlines } from './transform-inlines';
import { transformText } from './transform-text';

/**
 * Convert a document to a mdast tree.
 */
export async function gastToHast(
    document: Document | Fragment | Inline | Block,
    context: FormattingContext
): Promise<hast.Root> {
    const tree = await gastToAst<hast.Root>(document, context, {
        // @ts-expect-error
        transformBlocks,
        // @ts-expect-error
        transformInlines,
        transformText,
    });

    // Extract all footnote definitions as sections at the bottom of the document
    if (document.isDocument()) {
        const footnotes = context.getFootnotes();
        if (footnotes.length > 0) {
            tree.children.push(await transformFootnotesToHast(footnotes, context));
        }
    }

    return tree;
}

/**
 * Transform the footnote definitions as a section with an ol at the bottom of the document.
 * It mimics the GitHub markdown rendering.
 */
async function transformFootnotesToHast(
    footnotes: [string, Fragment][],
    context: FormattingContext
): Promise<hast.Element> {
    const list: hast.Element = {
        type: 'element',
        tagName: 'ol',
        properties: {},
        children: [],
    };

    const section: hast.Element = {
        type: 'element',
        tagName: 'section',
        properties: {
            dataFootnotes: true,
        },
        children: [list],
    };

    for (const [footnoteId, footnoteFragment] of footnotes) {
        const sectionTree = await gastToHast(footnoteFragment, context);

        list.children.push({
            type: 'element',
            tagName: 'li',
            properties: {
                id: footnoteIdToHtmlId(footnoteId),
            },
            children: sectionTree.children as hast.Element[],
        });
    }

    return section;
}
